import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import i18n from 'i18next';
import { useDebounce } from '../../core/utils/hooks/useDebounce';
import { Option } from '../../core/components/Fields/AutoCompleteField';
import { useApolloClient } from '@apollo/client';
import { useAssistanceContext } from '../pages/Assistance/AssistanceContext';
import { SEARCH_MASTERDATA_ENTITY_FIELD } from '../../masterdata/queries';
import { camelCase } from 'lodash';
import { useFocusTrap } from '../../core/utils/hooks/useLocation';
import { useTranslation } from 'react-i18next';
import { handleOpenMasterdataBrowser } from '../../masterdata/utils.tsx';

export const MASTERDATA_BROWSER_KEY = 'BROWSE';
export const MASTERDATA_DEFAULT_BROWSER_KEY = 'DEFAULT_BROWSE';
const MASTERDATA_BROWSER_KEYS = [MASTERDATA_BROWSER_KEY, MASTERDATA_DEFAULT_BROWSER_KEY];
const MASTERDATA_MAX_DROPDOWN_OPTIONS = 5;

/**
 * Assemble the decorated option list shared by every matching-enabled dropdown.
 */
export const buildMatchingDropdownOptions = ({
    headerLabel,
    items,
    message,
    browserButtons,
}: {
    headerLabel: React.ReactNode;
    items: Option[];
    message?: React.ReactNode;
    browserButtons: { label: string; value: string }[];
}): Option[] => {
    const options: Option[] = [{ role: 'message', label: headerLabel } as Option, ...items];
    if (message) {
        options.push({ role: 'message', label: message } as Option);
    }
    options.push({ role: 'separator' } as Option);
    browserButtons.forEach(({ label, value }) => {
        options.push({ role: 'button', label, value });
    });
    return options;
};

const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string

const HighlightedText = ({ highlight, children }) => {
    // Split text on highlight term, include term itself into parts, ignore case
    const parts = children.split(new RegExp(`(${escapeRegExp(highlight)})`, 'gi'));

    return (
        <>
            {parts.map((part, i) =>
                part.toLowerCase() === highlight.toLowerCase() ? (
                    <b key={i} className="font-semibold bg-surface-strong">
                        {part}
                    </b>
                ) : (
                    <span key={i}>{part}</span>
                )
            )}
        </>
    );
};

const formatLineItemInlineFields = (option: Option): string => {
    const lang = localStorage.getItem('i18nextLng') || 'en';
    const parts: string[] = [];

    if (option.positionNumber != null && option.positionNumber !== '') {
        parts.push(`${i18n.t('masterdata:assistance.fields.lineItemOption.pos')} ${option.positionNumber}`);
    }
    if (option.quantity != null && option.quantity !== '') {
        const qty = Number(option.quantity).toLocaleString(lang);
        parts.push(
            `${i18n.t('masterdata:assistance.fields.lineItemOption.qty')} ${qty}${option.unit ? ' ' + option.unit : ''}`
        );
    }
    if (option.totalPrice != null && option.totalPrice !== '') {
        const total = Number(option.totalPrice).toLocaleString(lang, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        parts.push(`${i18n.t('masterdata:assistance.fields.lineItemOption.price')} ${total}`);
    }

    return parts.join(' · ');
};

const formatLineItemDeliveryDate = (option: Option): string => {
    if (!option.deliveryDate) return '';
    const parsed = new Date(option.deliveryDate);
    if (isNaN(parsed.getTime())) return '';
    return `${i18n.t('masterdata:assistance.fields.lineItemOption.delivery')} ${format(parsed, 'P')}`;
};

export const renderArticleIdOption =
    (articleIdName: string) =>
    (option: Option, value: string): React.ReactNode => {
        const articleIds = [option.articleId1, option.articleId2, option.articleId3];
        const articleId = option[camelCase(articleIdName)] || '';
        const articleDescription = option.description || option.articleDescription || '';
        const otherArticleIdsText = articleIds
            .filter((id) => id) // filter non-null values
            .filter((id) => id != articleId)
            .join(', ');
        const inlineFieldsText = formatLineItemInlineFields(option);
        const deliveryDateText = formatLineItemDeliveryDate(option);

        return (
            <div className={option.disabled ? 'text-content-medium' : ''}>
                {option.message || (
                    <>
                        <div className="text-content-strong">
                            <HighlightedText highlight={value}>{option.label || option.value || ''}</HighlightedText>
                        </div>
                        {otherArticleIdsText && (
                            <div className="text-content-weak text-sm">
                                <HighlightedText highlight={value}>{otherArticleIdsText}</HighlightedText>
                            </div>
                        )}
                        {articleDescription && (
                            <div className="text-content-weak text-sm">
                                <HighlightedText highlight={value}>{articleDescription}</HighlightedText>
                            </div>
                        )}
                        {inlineFieldsText && <div className="text-content-weak text-sm">{inlineFieldsText}</div>}
                        {deliveryDateText && <div className="text-content-weak text-sm">{deliveryDateText}</div>}
                    </>
                )}
            </div>
        );
    };

const joinAddressFromResult = (option: Option) => {
    return [
        option.name,
        option.companyName,
        option.streetAndNr,
        option.streetName,
        option.streetNumber,
        option.postcode,
        option.city,
        option.countrySubdivision,
        option.country,
        option.email,
        option.phone,
        option.misc,
    ]
        .filter((part) => part)
        .join(', ');
};

export const renderAddressOption = (option: Option, value: string): React.ReactNode => {
    return (
        <div>
            <div className="text-content-strong">
                <HighlightedText highlight={value}>{option.label || option.value}</HighlightedText>
            </div>
            <div className="text-content-weak text-sm">
                <HighlightedText highlight={value}>{joinAddressFromResult(option)}</HighlightedText>
            </div>
        </div>
    );
};

export const renderClientOption = (option: Option, value: string): React.ReactNode => {
    return (
        <div onMouseDown={option.onMouseDown}>
            <div className="text-content-strong">
                <HighlightedText highlight={value}>{option.label || option.value}</HighlightedText>
            </div>
            <div className="text-content-weak text-sm">
                <HighlightedText highlight={value}>{joinAddressFromResult(option)}</HighlightedText>
            </div>
        </div>
    );
};

export const renderContactOption = (option: Option, value: string): React.ReactNode => {
    const primary = option.label || option.value;
    const secondary = option.fullName && option.fullName !== primary ? option.fullName : option.contactId;

    return (
        <div>
            <div className="text-content-strong">
                <HighlightedText highlight={value}>{primary}</HighlightedText>
            </div>
            {secondary && (
                <div className="text-content-weak text-sm">
                    <HighlightedText highlight={value}>{secondary}</HighlightedText>
                </div>
            )}
        </div>
    );
};

export const renderFrameworkContractOption = (option: Option, value: string): React.ReactNode => {
    return (
        <div>
            <div className="text-content-strong">
                <HighlightedText highlight={value}>{option.label || option.value}</HighlightedText>
            </div>
            <div className="text-content-weak text-sm">
                <HighlightedText
                    highlight={value}
                >{`${option.contractName} (${option.clientName}, ${option.clientId}) `}</HighlightedText>
            </div>
        </div>
    );
};

export const renderLookupOptionWithLineItemCount = (option: Option, value: string): React.ReactNode => {
    return (
        <div>
            <div className="text-content-strong">
                <HighlightedText highlight={value}>{option.label || option.value}</HighlightedText>
            </div>
            {option.lineItemCount != null && (
                <div className="text-content-weak text-sm">{option.lineItemCount} line items</div>
            )}
        </div>
    );
};

export const deduplicateLookupOptions = (options: Option[], keyField: string): Option[] => {
    const counts = new Map<string, number>();
    return options
        .filter((option) => {
            const key = option[keyField] || option.value;
            counts.set(key, (counts.get(key) ?? 0) + 1);
            return counts.get(key) === 1;
        })
        .map((option) => {
            const key = option[keyField] || option.value;
            return { ...option, lineItemCount: counts.get(key) };
        });
};

export const fetchLookupOptions =
    ({ client, lookupDefinitionId, lookupFieldName, partitionId, extraSearchTerms = {}, fetchPolicy = 'no-cache' }) =>
    (searchTerm: string, offset: number = 0, limit: number = 10) => {
        const parseItem = (node: any): Option => {
            return {
                value: node?.[camelCase(lookupFieldName)],
                ...node,
                // data is to support legacy browsable masterdata fields
                data: { _lookupEntity: node },
            };
        };

        return client
            .query({
                query: SEARCH_MASTERDATA_ENTITY_FIELD,
                variables: {
                    lookupDefinitionId,
                    targetField: lookupFieldName,
                    searchTerm,
                    partitionId: partitionId || null, // prevent sending empty value
                    from: offset,
                    size: limit,
                    kwargs: extraSearchTerms,
                },
                fetchPolicy,
            })
            .then((res: any) => {
                const results = res?.data?.assistanceSearchByField.edges.map((edge) => edge.node);
                return results.map(parseItem);
            })
            .catch((err) => {
                console.error(err);
                return [];
            });
    };

export const useLookupOptions = ({
    lookupDefinitionId,
    lookupFieldName,
    partitionId,
    searchTerm,
    extraSearchTerms,
    searchLookupOnEmptyValue,
    skip = false,
    fetchPolicy = 'no-cache',
    limit = 10,
}: any) => {
    const [options, setOptions] = useState<Option[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const client = useApolloClient();
    const { record } = useAssistanceContext();
    const recordId = record?.id;

    const fetchOptions = useCallback(
        (searchTerm: string, offset: number = 0, limit: number = 10) => {
            setIsLoading(true);

            return fetchLookupOptions({
                client,
                lookupDefinitionId,
                lookupFieldName,
                partitionId,
                extraSearchTerms: extraSearchTerms,
                fetchPolicy,
            })(searchTerm, offset, limit)
                .then((options: Option[]) => {
                    setOptions(options);
                    return options;
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [client, lookupDefinitionId, recordId, lookupFieldName, partitionId]
    );
    const debouncedFetchOptions = useDebounce(fetchOptions, 300);

    useEffect(() => {
        if (skip) return;

        if (!recordId || !lookupDefinitionId || (!searchLookupOnEmptyValue && searchTerm === '')) {
            setOptions([]);
            return;
        }

        // prevent showing stale results until debounced fetch is triggered
        setIsLoading(true);

        debouncedFetchOptions(searchTerm, 0, limit);
    }, [debouncedFetchOptions, searchTerm, searchLookupOnEmptyValue, lookupDefinitionId, recordId, skip]);

    return { options, isLoading };
};

export const useMasterdataBrowser = ({
    lookupType,
    lookupDefinitionId,
    lookupFieldName,
    partitionId,
    searchTerm,
    options,
    onSelectOption,
    onBlur,
    onFocus,
    isPartitionIdField = false,
    lookupMatchingProps = null,
    extractionSearchResults = null,
}: any) => {
    const { handlers } = useAssistanceContext();
    const { closeMasterdataBrowser } = handlers;

    const { t } = useTranslation('masterdata');
    const { trapFocus } = useFocusTrap();

    const [blurred, setBlurred] = useState(false);
    const [disableBlur, setDisableBlur] = useState(false);

    useEffect(() => {
        if (blurred) {
            if (!disableBlur) {
                onBlur?.(null);
            }

            setBlurred(false);
            setDisableBlur(false);
        }
    }, [blurred]);

    const fetchOptions = fetchLookupOptions({
        client: useApolloClient(),
        extraSearchTerms: lookupMatchingProps?.extraSearchTerms,
        lookupDefinitionId,
        lookupFieldName,
        partitionId,
    });

    const openBrowser = () =>
        handleOpenMasterdataBrowser({
            handlers: handlers,
            partitionId: partitionId,
            isPartitionIdField: isPartitionIdField,
            searchTerm: searchTerm,
            lookupType: lookupType,
            lookupDefinitionId: lookupDefinitionId,
            extraSearchTerms: lookupMatchingProps?.extraSearchTerms,
            targetField: lookupFieldName,
            hasMatching: lookupMatchingProps?.hasMatching,
            matchingDocumentNumber: lookupMatchingProps?.matchingDocumentNumber,
            lookupLastSuccessfulImportDate: lookupMatchingProps?.lookupLastSuccessfulImportDate,
            extractionSearchResults: extractionSearchResults,
            onSelectRecord: (record) => {
                onSelectOption?.({
                    value: record[camelCase(lookupFieldName)],
                    ...record,
                });
                closeMasterdataBrowser();
                setBlurred(true);
            },
        });

    // Open up the MD browser using the default lookup type belonging to the field type (no matching lookup)
    const openBrowserDefault = () =>
        handleOpenMasterdataBrowser({
            handlers: handlers,
            partitionId: partitionId,
            isPartitionIdField: isPartitionIdField,
            searchTerm: searchTerm,
            lookupType: lookupMatchingProps?.lookupTypeDefault,
            lookupDefinitionId: lookupMatchingProps?.lookupDefinitionIdDefault,
            extraSearchTerms: undefined,
            targetField: lookupMatchingProps?.lookupFieldNameDefault,
            onSelectRecord: (record) => {
                onSelectOption?.({
                    value: record[camelCase(lookupMatchingProps?.lookupFieldNameDefault)],
                    ...record,
                });
                closeMasterdataBrowser();
                setBlurred(true);
            },
        });

    // Data manipulation BEFORE slicing (e.g. deduplication)
    const processedOptions = lookupMatchingProps?.processOptions
        ? lookupMatchingProps.processOptions(options, searchTerm)
        : options;

    // Then, slice
    const truncateOptions = processedOptions.length > MASTERDATA_MAX_DROPDOWN_OPTIONS;
    const slicedOptions = truncateOptions
        ? processedOptions.slice(0, MASTERDATA_MAX_DROPDOWN_OPTIONS)
        : processedOptions;

    // Display rendering AFTER slicing
    let displayOptions: Option[];
    if (lookupMatchingProps?.renderOptions) {
        displayOptions = lookupMatchingProps.renderOptions(slicedOptions, searchTerm, truncateOptions);
    } else {
        const showBrowser = lookupMatchingProps?.alwaysShowBrowser || truncateOptions;
        displayOptions = showBrowser
            ? [...slicedOptions, { role: 'button', label: t('browser.openBrowser'), value: MASTERDATA_BROWSER_KEY }]
            : slicedOptions;
    }

    const handleFocus = (e) => {
        trapFocus({ current: e.target });
        onFocus?.(e);
    };

    const handleBlur = () => {
        // delayed blur to allow for click events to be processed
        setBlurred(true);
    };

    const handleSelectOption = (selectedOption) => {
        // prevent the value from turning into the browser key
        if (!MASTERDATA_BROWSER_KEYS.includes(selectedOption.value)) {
            onSelectOption(selectedOption);
        }
    };

    const handleSelect = (event: CustomEvent) => {
        // prevent the value from turning into the browser key
        if (MASTERDATA_BROWSER_KEYS.includes(event.detail.value)) {
            event.preventDefault();

            setDisableBlur(true);

            event.detail.value == MASTERDATA_BROWSER_KEY ? openBrowser() : openBrowserDefault();
        }
    };

    return {
        onBlur: handleBlur,
        onFocus: handleFocus,
        onSelectOption: handleSelectOption,
        onSelect: handleSelect,
        options: displayOptions,
    };
};

export const useExtractText = (query, resultFieldName) => {
    const client = useApolloClient();
    const { record } = useAssistanceContext();
    const recordId = record?.id;

    return useCallback(
        (bbox, pageIndex, isEmailOcr = false) => {
            return client
                .query({
                    query: query,
                    variables: {
                        recordId,
                        bbox,
                        pageIndex,
                        isEmailOcr,
                    },
                    fetchPolicy: 'no-cache',
                })
                .then((res) => res?.data?.[resultFieldName]?.text || '')
                .catch((err) => {
                    // Don't block whole view if invalid lookup keys
                    console.error(err);
                    return '';
                });
        },
        [client, recordId, query, resultFieldName]
    );
};
